/** Race rules and network timing shared by client and server. */
import type { Difficulty } from './levelgen/difficulty';

/** Levels of a race by levels unless the room says otherwise (the default race setting). */
export const LEVELS_PER_RACE = 10;

/** 'levels': first to complete every level wins; 'time': most levels when the time is up. */
export const RACE_FORMATS = ['levels', 'time'] as const;
export type RaceFormat = (typeof RACE_FORMATS)[number];

/** Lengths the room offers for a race by levels. */
export const RACE_LEVEL_OPTIONS = [3, 5, 10, 15, 20] as const;
/** Lengths the room offers for a race against the clock, seconds (3, 5 and 10 minutes). */
export const RACE_DURATION_OPTIONS = [180, 300, 600] as const;

/** Most levels a race may have: protocol bound and the cap of a race against the clock. */
export const MAX_RACE_LEVELS = 100;
/** Bounds of a race against the clock (server configuration, e.g. short ones in tests), seconds. */
export const MIN_RACE_SECONDS = 10;
export const MAX_RACE_SECONDS = 1200;

/** The race a room runs next; the player who starts races sets it between races. */
export interface RaceSettings {
  format: RaceFormat;
  /** Levels of a race by levels (kept while the format is 'time', for switching back). */
  levelCount: number;
  /** Length of a race against the clock, seconds (kept while the format is 'levels'). */
  durationSec: number;
  difficulty: Difficulty;
}

/** A new room's race: today's game, 10 levels of rising difficulty; 5 minutes against the clock. */
export const DEFAULT_RACE_SETTINGS: Readonly<RaceSettings> = Object.freeze({
  format: 'levels',
  levelCount: LEVELS_PER_RACE,
  durationSec: 300,
  difficulty: 'rising',
});

export function sameRaceSettings(a: Readonly<RaceSettings>, b: Readonly<RaceSettings>): boolean {
  return (
    a.format === b.format &&
    a.levelCount === b.levelCount &&
    a.durationSec === b.durationSec &&
    a.difficulty === b.difficulty
  );
}

/** Time between the `race.start` message (levels are sent) and the actual start. */
export const COUNTDOWN_MS = 4000;

/** How often a racing client reports its position. */
export const STATE_SEND_HZ = 15;

/**
 * At most this many position batches a second reach each member of a room: a position goes out
 * at once when the member's previous batch is older than a batch interval, otherwise with the
 * next batch (the latest position of every racer that moved meanwhile).
 */
export const STATE_BROADCAST_HZ = 20;

/** Voice chat is a peer-to-peer mesh, so rooms are kept small. */
export const MAX_ROOM_MEMBERS = 8;

/** Upper bound on simultaneously existing rooms (protects the server). */
export const MAX_ROOMS = 20;

/** The room every player joins right after logging in. It always exists. */
export const DEFAULT_ROOM_ID = 'main';
export const DEFAULT_ROOM_NAME = 'Общая комната';

/** A race is force-finished after this long (a long race by levels gets more, see the server). */
export const RACE_MAX_DURATION_MS = 30 * 60_000;

/** How long the results screen is shown before the room returns to the lobby. */
export const RESULTS_PHASE_MS = 20_000;

/**
 * A racer still in a running race keeps their room seat (and race progress) this long after
 * losing the connection or closing the page. Seats outside a race wait much less (see the
 * server's GameRules).
 */
export const RECONNECT_GRACE_MS = 120_000;
